/**
 * Reading a document that arrived from somewhere else.
 *
 * Every function here takes `unknown` and answers with a value plus the diagnostics that explain
 * what it refused. A malformed document produces a report, never a throw and never a partially
 * built form — the caller decides whether a lenient result is good enough.
 */
import { expressionContextKeys, expressionPaths, validateExpression, } from "../expression.js";
import { MDY_ID_DELIMITER, MDY_MAX_DYNAMIC_PATH_LENGTH, MDY_MAX_DYNAMIC_PATTERN_LENGTH, collectingDiagnostics, isFiniteNumber, isIsoDate, isRecordValue, hasInvisibleCharacters, isSafeDynamicName, isSafeDynamicSegment, warnDev, } from "./guards.js";
import { MDY_DYNAMIC_MEMBERS, unknownMembers, MDY_DYNAMIC_MEMBER_ARRIVALS } from "./members.js";
import { dynamicPatternRefusal } from "./pattern-cost.js";
import { explainValueMismatch } from "../value-contracts.js";
import { MDY_FIELD_KINDS } from "../field-kinds.js";
import { explainGranularityProblem, validateTimeGranularity } from "../time-granularity.js";
import { breaksValueConversion } from "../path-utils.js";
import { required } from "../validators.js";
import { mdyEmptyValueFor } from "./schema.js";
import { MDY_DYNAMIC_FIELD_KINDS, } from "./schema.js";
/**
 * A record rather than a list, so a size dropped here is a missing key and not a shorter array that
 * still type-checks. The constraint sits inside `Object.freeze` because a literal handed to a call
 * is no longer fresh, and an annotation on the binding would then accept a key the union has
 * dropped — catching a size added and missing one removed.
 *
 * The union stays spelled out because it is the published shape, and a shape read through an
 * inferred alias is one an external differ can no longer resolve.
 */
const MDY_DYNAMIC_BREAKPOINTS = Object.freeze({
    base: true,
    sm: true,
    md: true,
    lg: true,
});
const isBreakpoint = (size) => Object.hasOwn(MDY_DYNAMIC_BREAKPOINTS, size);
/** The most tracks a row may declare. Beyond this a row is not a layout, it is a table. */
const MDY_MAX_LAYOUT_COLUMNS = 12;
/**
 * How deep a layout may nest.
 *
 * A bound against a structure arriving from outside — a stored document, a configuration fetched at
 * runtime — that would otherwise drive unbounded recursion through a parse. That is the whole of
 * what it defends, and it is why the number is a constant rather than something a caller supplies:
 * a limit an attacker's input can raise is not a limit.
 *
 * It is deliberately far above any structure a person would author, and matches
 * `MDY_MAX_EXPRESSION_DEPTH` — the same kind of guard, on the same kind of input.
 *
 * **It is not a limit on how much a form may ask of somebody.** It was six, on the argument that
 * nobody answers a question whose applicability depends on six earlier answers — a good argument
 * about the wrong axis. Nesting is *arrangement*: six groups deep is "Address → Billing →
 * Registered office", and the field inside is active, visible and conditional on nothing. What that
 * argument describes is a chain of *rules*, which this constant has never governed. ADR 0161.
 */
export const MDY_LAYOUT_MAX_DEPTH = 32;
/**
 * Calendar presentation coming from a config file rather than from typed code.
 *
 * The locale is checked because a malformed tag does not degrade — `Intl` throws a `RangeError`,
 * and a config that reached a renderer with `"en_US"` in it would take the form down at mount
 * rather than render an approximate calendar.
 */
function hasValidCalendarOptions(field, name) {
    if (field.locale !== undefined) {
        let usable = typeof field.locale === "string";
        if (usable) {
            try {
                Intl.getCanonicalLocales(field.locale);
            }
            catch {
                usable = false;
            }
        }
        if (!usable) {
            warnDev(`Dropped dynamic field "${name}": locale must be a valid BCP 47 tag, e.g. "it-IT".`, undefined, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
    }
    if (field.firstDayOfWeek !== undefined) {
        const day = field.firstDayOfWeek;
        if (!isFiniteNumber(day) || !Number.isInteger(day) || day < 0 || day > 6) {
            warnDev(`Dropped dynamic field "${name}": firstDayOfWeek must be an integer from 0 (Sunday) to 6.`, undefined, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
    }
    for (const bound of ["minDate", "maxDate"]) {
        if (field[bound] !== undefined && !isIsoDate(field[bound])) {
            warnDev(`Dropped dynamic field "${name}": ${bound} must be an ISO date, e.g. "2026-08-02".`, undefined, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
    }
    if (field.minDate !== undefined && field.maxDate !== undefined && field.minDate > field.maxDate) {
        warnDev(`Dropped dynamic field "${name}": minDate cannot be later than maxDate.`, undefined, MDY_DYNAMIC_INVALID_FIELD);
        return false;
    }
    return true;
}
/**
 * A comparable spelling of an option's value.
 *
 * A value may be a scalar or an object keyed by what it holds (ADR 0051), and two objects declaring
 * the same members are the same option however they were written — so the key is the members in a
 * fixed order rather than the reference.
 */
function optionValueKey(value) {
    // JSON spells both infinities `null`, which would make a value past a double's range in either
    // direction a duplicate of the other.
    if (typeof value === "number" && !Number.isFinite(value))
        return String(value);
    if (value === null || typeof value !== "object")
        return JSON.stringify(value) ?? String(value);
    const entries = Object.entries(value)
        .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
        .map(([key, held]) => `${JSON.stringify(key)}:${optionValueKey(held)}`);
    return `{${entries.join(",")}}`;
}
function hasValidOptions(options) {
    if (!Array.isArray(options))
        return false;
    return options.every((option) => {
        if (typeof option !== "object" || option === null)
            return false;
        const candidate = option;
        if (!("value" in candidate))
            return false;
        // The shapes the published schema allows: a scalar, or an object keyed by what it holds
        // (ADR 0051, which is why an object is a legitimate option value and not a mistake).
        //
        // `null` is refused because it is the empty value of half the kinds — an option meaning "nothing"
        // cannot be told apart from no choice at all. An array is refused because the schema an author's
        // editor checks against does not allow one, and a document the editor underlines while the
        // runtime accepts is a document whose two readers disagree.
        const value = candidate.value;
        if (value === null || Array.isArray(value))
            return false;
        if (!["string", "number", "boolean", "object"].includes(typeof value))
            return false;
        if (typeof candidate.label !== "string")
            return false;
        if (candidate.disabled !== undefined &&
            typeof candidate.disabled !== "boolean") {
            return false;
        }
        return true;
    });
}
/** The rules a field declares, and so the only keys its messages may name. */
const MDY_VALIDATOR_MESSAGE_KEYS = new Set([
    "required", "email", "integer", "min", "max", "minLength", "maxLength", "pattern",
]);
function hasValidValidatorConfig(validators, fieldName) {
    if (validators === undefined)
        return true;
    if (typeof validators !== "object" || validators === null || Array.isArray(validators)) {
        warnDev(`Dropped dynamic field "${fieldName}": validators must be an object.`, undefined, MDY_DYNAMIC_INVALID_FIELD);
        return false;
    }
    const config = validators;
    const boolKeys = ["required", "email", "integer"];
    for (const key of boolKeys) {
        const value = config[key];
        if (value !== undefined && typeof value !== "boolean") {
            warnDev(`Dropped dynamic field "${fieldName}": validators.${key} must be a boolean.`, undefined, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
    }
    const numberKeys = ["min", "max", "minLength", "maxLength"];
    for (const key of numberKeys) {
        const value = config[key];
        if (value !== undefined && !isFiniteNumber(value)) {
            warnDev(`Dropped dynamic field "${fieldName}": validators.${key} must be a finite number.`, undefined, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
    }
    // The words an author wrote for their own rules. Checked like everything else a document declares:
    // a message that is not a string is a sentence nobody can read, which is the thing this slot exists
    // to prevent.
    if (config.messages !== undefined) {
        if (!isRecordValue(config.messages)) {
            warnDev(`Dropped dynamic field "${fieldName}": validators.messages must be an object.`, undefined, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        for (const [key, said] of Object.entries(config.messages)) {
            if (!MDY_VALIDATOR_MESSAGE_KEYS.has(key)) {
                warnDev(`Dropped dynamic field "${fieldName}": validators.messages names "${key}", which is not a ` +
                    "rule a field declares.", undefined, MDY_DYNAMIC_INVALID_FIELD);
                return false;
            }
            if (typeof said !== "string" || said.trim() === "") {
                warnDev(`Dropped dynamic field "${fieldName}": validators.messages.${key} must be a message a ` +
                    "person can read.", undefined, MDY_DYNAMIC_INVALID_FIELD);
                return false;
            }
        }
    }
    if (config.minLength !== undefined &&
        config.maxLength !== undefined &&
        config.minLength > config.maxLength) {
        warnDev(`Dropped dynamic field "${fieldName}": validators.minLength cannot exceed validators.maxLength.`, undefined, MDY_DYNAMIC_INVALID_FIELD);
        return false;
    }
    if (config.pattern !== undefined) {
        if (typeof config.pattern !== "string") {
            warnDev(`Dropped dynamic field "${fieldName}": validators.pattern must be a string.`, undefined, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        if (config.pattern.length > MDY_MAX_DYNAMIC_PATTERN_LENGTH) {
            warnDev(`Dropped dynamic field "${fieldName}": validators.pattern length exceeds max ${MDY_MAX_DYNAMIC_PATTERN_LENGTH}.`, undefined, "MDY_DYNAMIC_PATTERN_TOO_LONG");
            return false;
        }
        // Reported where the document is read, and the field stays: one rule the engine will not run is
        // not a reason to take an input away from the person filling the form. The rule itself is
        // refused where validators are built, which is the only place that can refuse it for every
        // caller rather than only for this parse.
        const refusal = dynamicPatternRefusal(config.pattern);
        if (refusal !== null) {
            warnDev(`Dropped validators.pattern on dynamic field "${fieldName}": the pattern has ${refusal}.`, undefined, "MDY_DYNAMIC_PATTERN_TOO_COSTLY");
        }
        else {
            // And one the platform cannot compile at all. The cost gate above answers about a pattern that
            // runs too long; `[` does not run: the validator is skipped where it is built, and a document
            // carrying it passed strict mode with nothing said — a publishing gate approving a rule that
            // will never exist.
            try {
                new RegExp(config.pattern);
            }
            catch {
                warnDev(`Dropped validators.pattern on dynamic field "${fieldName}": "${config.pattern}" is not a ` +
                    "regular expression this platform can compile, so the rule never runs.", undefined, MDY_DYNAMIC_INVALID_FIELD);
            }
        }
    }
    return true;
}
/**
 * The fields a nested schema flattens to, and the collections that flattening walked through.
 *
 * A path cannot say which it came from: `lines.0` reads as the key `"0"` whether the document
 * declared an array or a record keyed by digits. Reporting the collections alongside the fields is
 * what lets a consumer rebuild the shape the document declared instead of guessing it — and
 * guessing is the one thing that cannot be made safe, because both readings are legitimate.
 */
/**
 * How many fields a schema says it has, counted without trusting it.
 *
 * Read off the raw object over an explicit stack, because this runs on a document the validator may
 * have already refused: it must not recurse on a shape it was handed, and it must not assume any
 * node is well formed. A node that is neither a field nor a container it can descend counts as one
 * declaration — something was written there, and it did not become a field.
 */
function declaredFieldCount(schema) {
    let count = 0;
    const stack = [schema];
    let complete = true;
    // A bound, because the count is taken from a raw object rather than from a validated one, and a
    // walk over a shape nothing has checked must be able to stop. It is an order of magnitude above
    // the size at which a saturating count first reported a document as having lost a fifth of what it
    // lost, and when the walk does stop the counts say so rather than passing a floor off as a total.
    for (let steps = 0; stack.length > 0; steps += 1) {
        if (steps >= MDY_MAX_DECLARATION_WALK) {
            complete = false;
            break;
        }
        const node = stack.pop();
        // Absent is a collection with no item, which declared nothing; anything else written where a
        // node goes is a declaration this reader cannot use, and is counted as one.
        if (node === undefined)
            continue;
        if (!isRecordValue(node)) {
            count += 1;
            continue;
        }
        const kind = node["node"];
        if (kind === "field") {
            count += 1;
            continue;
        }
        if (kind === "group") {
            const children = node["children"];
            if (isRecordValue(children))
                stack.push(...Object.values(children));
            else
                count += 1;
            continue;
        }
        // A collection is *understood*, not lost — it is reported by path and kind in `collections` — and
        // what is inside it is not nothing: a cell is declared, and it legitimately never becomes a flat
        // field because a document cannot name rows that do not exist yet. The pair is the one place
        // that says it was declared at all, so the walk descends and the collection itself counts as
        // neither.
        if (kind === "array" || kind === "record") {
            stack.push(node["item"]);
            continue;
        }
        // Something was declared here and it is not a node this reader knows.
        count += 1;
    }
    return { count, complete };
}
/**
 * How many declarations the count will walk before it stops.
 *
 * A document at this size is refused for every other reason before its counts matter; the number is
 * here so that a shape nothing has validated cannot make the walk run without end.
 */
const MDY_MAX_DECLARATION_WALK = 100_000;
/**
 * How many visits one flatten may make beyond the nodes the schema declares.
 *
 * A row that does not write a nested collection takes that collection's declared rows, so what a
 * flatten visits is the product of the row counts down each branch and not the size of the document:
 * a few kilobytes can name more nodes than a request can hold. Every visit counts — a group, a
 * collection, a leaf, a row template's nodes, a leaf refused on the walk — because a branch that ends
 * in no field costs the same walk as one that ends in a field.
 *
 * Measured beyond the declared nodes rather than as a total, so a document the size of what it
 * declares is never refused for its size: what is bounded is what rows multiply, and the walk stays
 * within a constant of the document. The number is ten times the largest form the scale benchmark
 * measures, and room for a hundred rows of a couple of dozen cells each.
 */
const MDY_MAX_DYNAMIC_SCHEMA_VISITS = 10_000;
/**
 * How many rows a collection's own `initialValue` may write.
 *
 * The published JSON Schema states it as `maxItems` on an array's and `maxProperties` on a record's
 * `initialValue`, and every reader refuses past it. A document that writes more is not a form the
 * contract describes, so the whole schema is refused, like any other finding about the tree's shape.
 */
const MDY_MAX_DYNAMIC_WRITTEN_ROWS = 100;
const writtenRowsLimit = (path, kind) => ({
    code: "MDY_DYNAMIC_SCHEMA_LIMIT",
    severity: "error",
    path: `${path}/initialValue`,
    message: `${kind} initialValue exceeds ${MDY_MAX_DYNAMIC_WRITTEN_ROWS} rows.`,
});
/**
 * Every node object a schema declares, through `children` and `item`, without reading any row.
 *
 * An upper bound on the first visit of each node, which is what the flatten budget is measured
 * beyond. It counts what the walk would skip as well — a child under an unsafe key, a member that is
 * not a node — because the bound only has to be no smaller than what the document wrote.
 */
function declaredNodeCount(schema) {
    let count = 0;
    const pending = [schema];
    while (pending.length > 0) {
        const node = pending.pop();
        if (!isRecordValue(node))
            continue;
        count += 1;
        if (node["node"] === "group" && isRecordValue(node["children"]))
            pending.push(...Object.values(node["children"]));
        else if (node["node"] === "array" || node["node"] === "record")
            pending.push(node["item"]);
    }
    return count;
}
/**
 * A document whose flatten visits more nodes than a reader carries.
 *
 * Thrown rather than truncated: the part of a tree a walk happens to reach first is not a form the
 * document declared, so there is no partial answer to give. `parseDynamicForm` reports it as the
 * `MDY_DYNAMIC_SCHEMA_LIMIT` diagnostic at `/schema` and accepts nothing from the schema.
 */
export class MdyDynamicSchemaLimitError extends Error {
    limit;
    code = "MDY_DYNAMIC_SCHEMA_LIMIT";
    path = "/schema";
    constructor(limit) {
        super(schemaLimitMessage(limit));
        this.limit = limit;
        this.name = "MdyDynamicSchemaLimitError";
    }
}
function schemaLimitMessage(limit) {
    return `the schema's rows expand it by more than ${limit} nodes beyond those it declares, more than a reader carries.`;
}
export function flattenDynamicForm(schema) {
    return flattenTree(schema, true);
}
/**
 * The flatten, told whether the declarations have already been judged.
 *
 * A caller that validated the tree first has already reported every field that declares something
 * unusable, at the path of its declaration and under its own name. Reporting it again from here
 * names it by the stand-in the leaf is checked under and points at a list the document never wrote,
 * so the second finding reads as a second defect. What only the walk can see is still reported: a
 * row's initial value that the cell's kind cannot hold, and a path too long to carry.
 */
function flattenTree(schema, judgeDeclarations) {
    const fields = [];
    const collections = [];
    /**
     * The row templates, keyed by the item node.
     *
     * Keyed by node so a collection inside a row is flattened once and not once per row — the shape
     * belongs to the node, and a list of a thousand rows would otherwise pay for the same template a
     * thousand times. The queue keeps the templates a flat piece of work: a document nests as deep as
     * it likes, and a walk that descended into each item as it met it would spend the stack the
     * iterative walk below exists to protect.
     */
    const templates = new Map();
    const queue = [];
    const templateOf = (item, at) => {
        const held = templates.get(item);
        if (held)
            return held;
        // Handed out empty and filled when the queue reaches it: a template is a row's shape, and an
        // item that reaches itself is answered rather than followed forever.
        const fresh = { fields: [], collections: [] };
        templates.set(item, fresh);
        queue.push({ item, at });
        return fresh;
    };
    // One budget for the whole flatten, templates included: the bound is on the work a document can
    // cause, and a template walked separately is work all the same.
    const budget = { left: declaredNodeCount(schema) + MDY_MAX_DYNAMIC_SCHEMA_VISITS };
    flattenNode(schema, "/schema", fields, collections, templateOf, judgeDeclarations, budget);
    while (queue.length > 0) {
        const { item, at } = queue.pop();
        const held = templates.get(item);
        flattenNode(item, at, held.fields, held.collections, templateOf, judgeDeclarations, budget);
    }
    return { fields, collections };
}
/**
 * The walk itself: one node's subtree into the flat pair, with row templates asked for by node.
 *
 * `at` is the JSON Pointer of the node in the document, carried beside the dotted path because the
 * two diverge: a row's cell is declared once under the collection's `item` and named once per row.
 */
function flattenNode(root, rootAt, out, collections, templateOf, judgeDeclarations, budget) {
    /**
     * An explicit stack, for the reason the validator has one: a document is untrusted and its nesting
     * has no cap, so a recursive walk lets the document decide how much stack to use. A `RangeError`
     * carries no path, cannot be caught by name and looks exactly like a bug in the caller's own code
     * — the parser's own answer for a document it cannot carry is a diagnostic.
     *
     * Children are pushed in reverse so they come off in the order the document declares them: the
     * flat list a consumer reads is in document order, and a form built from it renders in that order.
     */
    const pending = [
        { node: root, path: "", at: rootAt, initial: undefined },
    ];
    while (pending.length > 0) {
        const { node, path, at, initial } = pending.pop();
        if (budget.left === 0)
            throw new MdyDynamicSchemaLimitError(MDY_MAX_DYNAMIC_SCHEMA_VISITS);
        budget.left -= 1;
        if (node.node === "field") {
            if (path.length > MDY_MAX_DYNAMIC_PATH_LENGTH) {
                warnDev(`Dropped a dynamic field whose path is ${path.length} characters, past the ` +
                    `${MDY_MAX_DYNAMIC_PATH_LENGTH} a path may be: a path is the payload key, the draft key ` +
                    "and the widget id, and every read of the value carries it.", `${at}/field`, "MDY_DYNAMIC_PATH_TOO_LONG");
                continue;
            }
            // Generated dotted/index paths are trusted structure; validate the leaf with
            // a temporary safe name, then restore the generated path.
            if (judgeDeclarations) {
                const candidate = { ...node.field, name: "leaf", initialValue: initial ?? node.field.initialValue };
                const parsed = parseDynamicFields([candidate]);
                if (parsed[0])
                    out.push({ ...parsed[0], name: path });
                continue;
            }
            // The declaration has been judged and reported; what is left is the value a row gave the cell,
            // which the declaration never saw.
            const rowInitial = initial ?? undefined;
            const declared = collectingDiagnostics(() => undefined, () => parseDynamicFields([{ ...node.field, name: "leaf" }]))[0];
            if (!declared)
                continue;
            if (rowInitial === undefined) {
                out.push({ ...declared, name: path });
                continue;
            }
            const mismatch = MDY_FIELD_KINDS.includes(declared.kind)
                ? explainValueMismatch(declared.kind, rowInitial)
                : null;
            if (mismatch !== null) {
                warnDev(`Dropped dynamic field "${path}": initialValue does not match the kind — ${mismatch}.`, `${at}/field`, MDY_DYNAMIC_INVALID_FIELD);
                continue;
            }
            out.push({ ...declared, name: path, initialValue: rowInitial });
            continue;
        }
        if (node.node === "group") {
            const value = isRecordValue(initial) ? initial : {};
            // A child that is not a node was reported where the tree was judged, and has nothing to walk.
            const entries = Object.entries(node.children)
                .filter(([key, child]) => isSafeDynamicName(key) && isDynamicNode(child));
            for (let index = entries.length - 1; index >= 0; index -= 1) {
                const [key, child] = entries[index];
                // Own members only: a child named `valueOf` reads the initial the document wrote for it, not
                // the method every object inherits under that name.
                const held = Object.hasOwn(value, key) ? value[key] : undefined;
                pending.push({
                    node: child,
                    path: path ? `${path}.${key}` : key,
                    at: `${at}/children/${pointerSegment(key)}`,
                    initial: held,
                });
            }
            continue;
        }
        if (node.node === "record") {
            collections.push({ path, kind: "record", item: templateOf(node.item, `${at}/item`) });
            const declared = isRecordValue(initial)
                ? initial
                : isRecordValue(node.initialValue) ? node.initialValue : {};
            // The key is a path segment like any other, and an unsafe one addresses something else. A row
            // key becomes part of a widget id here, so the whole name rule applies to it: flattened with
            // only the prototype half checked, `rows.  .cell` reached the flat builder, which refuses it —
            // and the same document built through the tree, where a row key is data and never a name.
            const rows = Object.entries(declared).filter(([key]) => isSafeDynamicName(key));
            for (let index = rows.length - 1; index >= 0; index -= 1) {
                const [key, row] = rows[index];
                pending.push({ node: node.item, path: path ? `${path}.${key}` : key, at: `${at}/item`, initial: row });
            }
            continue;
        }
        collections.push({ path, kind: "array", item: templateOf(node.item, `${at}/item`) });
        const rows = Array.isArray(initial) ? initial : Array.isArray(node.initialValue) ? node.initialValue : [];
        for (let index = rows.length - 1; index >= 0; index -= 1) {
            pending.push({ node: node.item, path: `${path}.${index}`, at: `${at}/item`, initial: rows[index] });
        }
    }
}
/**
 * Whether a value is a node the walk can enter, judged by its `node` member alone.
 *
 * The member must be the string itself: `["field"]` converts to `"field"`, and a node accepted on
 * that reading is walked as whichever branch its other members happen to fit.
 */
function isDynamicNode(value) {
    const kind = isRecordValue(value) ? value["node"] : undefined;
    return typeof kind === "string" && ["field", "group", "array", "record"].includes(kind);
}
/**
 * A key as one segment of a diagnostic path, escaped as RFC 6901 escapes a JSON Pointer token.
 *
 * "/" and "~" are legal in a name, so a key written raw splits into segments that address
 * something else: child `a/b` would read as child `a`, then `b`. `~` is escaped first, or the `~1`
 * written for a "/" would itself be rewritten.
 */
function pointerSegment(key) {
    return key.replaceAll("~", "~0").replaceAll("/", "~1");
}
/**
 * Where a field list entry was written, as a diagnostic path: the first object carrying the name.
 * The reader claims a name before any other check, so a later entry with it is the refused duplicate.
 */
function listEntryPath(list, name) {
    if (!Array.isArray(list))
        return undefined;
    const index = list.findIndex((item) => typeof item === "object" && item !== null && item.name === name);
    return index < 0 ? undefined : `/fields/${index}`;
}
/**
 * Every leaf a tree declares, with the path of its `field` member, in document order.
 *
 * A declaration, not a row: a collection's `item` is one template every row is read from, and it is
 * declared — and so held to the document's version — whether the collection starts with three rows
 * or none.
 */
function fieldDeclarations(schema) {
    const out = [];
    const pending = [{ node: schema, path: "/schema" }];
    while (pending.length > 0) {
        const { node, path } = pending.pop();
        if (!isRecordValue(node))
            continue;
        if (node["node"] === "field") {
            out.push({ field: node["field"], path: `${path}/field` });
        }
        else if (node["node"] === "group" && isRecordValue(node["children"])) {
            const children = Object.entries(node["children"]).map(([key, child]) => ({ node: child, path: `${path}/children/${pointerSegment(key)}` }));
            pending.push(...children.reverse());
        }
        else if (node["node"] === "array" || node["node"] === "record") {
            pending.push({ node: node["item"], path: `${path}/item` });
        }
    }
    return out;
}
/** The fields alone — {@link flattenDynamicForm} also reports the collections they came from. */
export function flattenDynamicSchema(schema) {
    return flattenDynamicForm(schema).fields;
}
/**
 * Whether `path` names something a validation may read or attach to.
 *
 * Wider than a rule's field reference, and deliberately: a rule fires an effect on one control, so
 * it names a leaf. A validation is about a *relationship*, and the thing it is about is often a
 * group or an array — "the basket must not be empty" is a condition on `items`, which is not a leaf
 * and never appears in the flattened field list. Accepting only leaves rejected exactly the
 * cross-field rules the slot exists to carry.
 */
function validValidationPath(path, names) {
    if (typeof path !== "string" || path === "")
        return false;
    if (names.has(path))
        return true;
    // A container: something in the form lives underneath it.
    const prefix = `${path}.`;
    for (const name of names)
        if (name.startsWith(prefix))
            return true;
    return false;
}
/**
 * The versions this contract reads, and the sentence that names them.
 *
 * Declared once because it is said in four places — the type union, the two refusals a person
 * reads, and the check that accepts an envelope. A list spelled out in each is a list that agrees
 * until somebody adds a version and updates three of them.
 */
const SUPPORTED_VERSIONS = [2, 3, 4, 5];
const SUPPORTED_VERSIONS_PHRASE = `${SUPPORTED_VERSIONS.slice(0, -1).join(", ")} and ${SUPPORTED_VERSIONS[SUPPORTED_VERSIONS.length - 1]}`;
const isSupportedVersion = (value) => SUPPORTED_VERSIONS.includes(value);
/**
 * Validates an untrusted (network/CMS) payload into `MdyDynamicField[]`.
 * TypeScript types do not check runtime JSON — this does.
 *
 * Accepts either a bare field array or a versioned
 * {@link MdyDynamicFormConfig} envelope (unknown versions are rejected).
 * Malformed entries and unknown `kind`s are dropped with a dev-mode warning,
 * so a partially-bad config still renders its valid fields.
 */
export function parseDynamicFields(input) {
    let items;
    if (Array.isArray(input)) {
        items = input;
    }
    else if (typeof input === "object" &&
        input !== null &&
        "fields" in input) {
        const envelope = input;
        if (!isSupportedVersion(envelope.version)) {
            warnDev(`Unsupported dynamic form config version ${String(envelope.version)} — this contract has ` +
                `versions ${SUPPORTED_VERSIONS_PHRASE}.` +
                (envelope.version === 1
                    ? ' Version 1 was read by this runtime alone and is no longer supported — set "version": 2, ' +
                        "which reads the same fields."
                    : ""), undefined, "MDY_DYNAMIC_UNSUPPORTED_VERSION");
            return [];
        }
        items = envelope.fields;
    }
    if (!Array.isArray(items)) {
        warnDev("Dynamic form config is neither a field array nor a config envelope.", undefined, MDY_DYNAMIC_INVALID_FIELD);
        return [];
    }
    const seenNames = new Set();
    /** Option lists this read shortened, by the entry they belong to. */
    const dedupedOptions = new Map();
    /** Fields whose declared granularity cannot be honoured, stripped on the way out rather than in place. */
    const droppedGranularity = new Set();
    /** Fields whose declared clock is not one this contract draws, dropped on the way out rather than in place. */
    const droppedFormat = new Set();
    /** Fields whose declared opening view is not one that exists, dropped on the way out rather than in place. */
    const droppedViewMode = new Set();
    const accepted = items.filter((item, index) => {
        // Where this entry is written, so a finding underlines the entry rather than the array. A
        // duplicate names the *second* occurrence: the first is legitimate until the second exists, and
        // the second is the one a reader has to change.
        const at = `/fields/${index}`;
        if (typeof item !== "object" || item === null) {
            warnDev(`Dropped non-object dynamic field: ${JSON.stringify(item)}`, at, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        const f = item;
        if (typeof f.name !== "string" || f.name.length === 0) {
            warnDev(`Dropped dynamic field without a name: ${JSON.stringify(item)}`, at, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        if (!isSafeDynamicSegment(f.name)) {
            warnDev(`Dropped dynamic field "${f.name}": name is reserved or contains forbidden path separators.`, at, "MDY_DYNAMIC_UNSAFE_NAME");
            return false;
        }
        // A character that cannot be seen makes two different names read the same, and a name is what a
        // value is filed under — the duplicate check exists for exactly this collision and would not see
        // it. The value sanitizer removes this class for the same reason.
        if (hasInvisibleCharacters(f.name)) {
            warnDev(`Dropped dynamic field ${JSON.stringify(f.name)}: the name carries a character that cannot ` +
                "be seen, so two names that read the same would be two different fields.", at, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        // The same cap the nested door applies, asked here too. This is the door an untrusted document
        // arrives at — `fields: [{ name, kind, label }]` is the whole of version 1 and the field half of
        // every version after it — and it was the one with no ceiling, so a name of a hundred thousand
        // characters was accepted without a word. The cost is not in the parse: it is what the constant
        // names, paid again at every read of every value, because the path is the payload key, the draft
        // key, the widget id and a string every renderer carries per field.
        if (f.name.length > MDY_MAX_DYNAMIC_PATH_LENGTH) {
            warnDev(`Dropped a dynamic field whose path is ${f.name.length} characters, past the ` +
                `${MDY_MAX_DYNAMIC_PATH_LENGTH} a path may be: a path is the payload key, the draft key ` +
                "and the widget id, and every read of the value carries it.", at, "MDY_DYNAMIC_PATH_TOO_LONG");
            return false;
        }
        // A form's value is an object, and a field named `toString` becomes a data property of it — so
        // `${value}` and `String(value)` throw `Cannot convert object to primitive value` in the
        // consumer's own code, naming neither the field nor the document. `JSON.stringify` is fine,
        // which is why it went unseen: serialization is the path everybody tries.
        if (breaksValueConversion(f.name)) {
            warnDev(`Dropped dynamic field "${f.name}": a form's value is an object, so this name shadows the ` +
                "method every string conversion of that value goes through, and `${value}` then throws.", at, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        if (f.name.includes(MDY_ID_DELIMITER)) {
            warnDev(`Dropped dynamic field "${f.name}": "${MDY_ID_DELIMITER}" separates the segments of a generated id, ` +
                `so this name would collide with another field's parts.`, at, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        // The other half of the sentence the renderer refuses on, and it was the half nobody applied. A
        // widget id is built from this name and reaches `aria-describedby`, which is a space-separated
        // list of ids — whitespace there becomes several references, each resolving to nothing. An
        // author ran the gate, was told the document was fine, saved it, and the field never appeared.
        if (/\s/.test(f.name)) {
            warnDev(`Dropped dynamic field "${f.name}": a widget id is built from this name, and whitespace ` +
                `splits an id reference into several, each resolving to nothing — so the control would ` +
                `have no accessible name.`, at, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        if (seenNames.has(f.name)) {
            warnDev(`Dropped duplicate dynamic field name "${f.name}".`, at, "MDY_DYNAMIC_DUPLICATE_NAME");
            return false;
        }
        seenNames.add(f.name);
        if (!MDY_DYNAMIC_FIELD_KINDS.includes(f.kind)) {
            warnDev(`Dropped dynamic field "${f.name}" with unknown kind "${String(f.kind)}".`, at, "MDY_DYNAMIC_UNKNOWN_KIND");
            return false;
        }
        if (f.label !== undefined && typeof f.label !== "string") {
            warnDev(`Dropped dynamic field "${f.name}": label must be a string.`, at, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        if (f.sensitive !== undefined && typeof f.sensitive !== "boolean") {
            warnDev(`Dropped dynamic field "${f.name}": sensitive must be a boolean.`, at, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        if (f.placeholder !== undefined && typeof f.placeholder !== "string") {
            warnDev(`Dropped dynamic field "${f.name}": placeholder must be a string.`, at, MDY_DYNAMIC_INVALID_FIELD);
            return false;
        }
        if (!hasValidValidatorConfig(f.validators, f.name)) {
            return false;
        }
        // Members nobody declared, on the field and on the objects it carries. The field is kept: a
        // reader may meet a document written against a contract it predates, and dropping the field
        // would turn a report into a loss. What is not kept is the silence.
        for (const [value, declared, what] of [
            [item, MDY_DYNAMIC_MEMBERS.field, `dynamic field "${f.name}"`],
            [f.validators, MDY_DYNAMIC_MEMBERS.validators, `the validators of "${f.name}"`],
        ]) {
            const unknown = unknownMembers(value, declared);
            if (unknown.length > 0) {
                warnDev(`${what} carries ${unknown.map((member) => JSON.stringify(member)).join(", ")}, which ` +
                    "this contract does not declare, so nothing reads it.", at, "MDY_DYNAMIC_UNKNOWN_MEMBER");
            }
        }
        if (Array.isArray(f.options)) {
            for (const [index, option] of (f.options).entries()) {
                const unknown = unknownMembers(option, MDY_DYNAMIC_MEMBERS.option);
                if (unknown.length > 0) {
                    warnDev(`an option of "${f.name}" carries ${unknown.map((member) => JSON.stringify(member)).join(", ")}, ` +
                        "which this contract does not declare, so nothing reads it.", `${at}/options/${index}`, "MDY_DYNAMIC_UNKNOWN_MEMBER");
                }
            }
        }
        if (f.kind === "number" || f.kind === "slider") {
            const numberField = f;
            if (numberField.min !== undefined && !isFiniteNumber(numberField.min)) {
                warnDev(`Dropped dynamic field "${f.name}": min must be a finite number.`, at, MDY_DYNAMIC_INVALID_FIELD);
                return false;
            }
            if (numberField.max !== undefined && !isFiniteNumber(numberField.max)) {
                warnDev(`Dropped dynamic field "${f.name}": max must be a finite number.`, at, MDY_DYNAMIC_INVALID_FIELD);
                return false;
            }
            if (numberField.min !== undefined &&
                numberField.max !== undefined &&
                numberField.min > numberField.max) {
                warnDev(`Dropped dynamic field "${f.name}": min cannot exceed max.`, at, MDY_DYNAMIC_INVALID_FIELD);
                return false;
            }
            if (numberField.step !== undefined) {
                if (!isFiniteNumber(numberField.step)) {
                    warnDev(`Dropped dynamic field "${f.name}": step must be a finite number.`, at, MDY_DYNAMIC_INVALID_FIELD);
                    return false;
                }
                if (numberField.step <= 0) {
                    warnDev(`Dropped dynamic field "${f.name}": step must be greater than zero.`, at, MDY_DYNAMIC_INVALID_FIELD);
                    return false;
                }
            }
        }
        if (f.kind === "datepicker" || f.kind === "timepicker" || f.kind === "daterange") {
            if (!hasValidCalendarOptions(f, f.name))
                return false;
        }
        // The initial the document declares, against the shape the kind holds. A collection's initial is
        // measured against its own shape — a record wants an object, an array a list, each refused by
        // name — and a field's was measured against nothing: a text field declaring `42` produced a
        // document that passed in strict mode and a form that was invalid before anyone touched it, with
        // "This field holds string" on a value the user never entered.
        //
        // The knowledge was published and used one layer later. `explainValueMismatch` is the same
        // sentence the engine says about a value that arrives at runtime; a declared initial is that
        // value, arriving earlier.
        if (f.initialValue !== undefined && MDY_FIELD_KINDS.includes(f.kind)) {
            const mismatch = explainValueMismatch(f.kind, f.initialValue);
            if (mismatch !== null) {
                warnDev(`Dropped dynamic field "${f.name}": initialValue does not match the kind — ${mismatch}.`, at, MDY_DYNAMIC_INVALID_FIELD);
                return false;
            }
        }
        // A constraint written one level too high. `validators: { required: true }` is the contract's
        // spelling, and `required: true` on the field is what an author — or a model writing the
        // document — reaches for instead: it is kept, nothing reads it, and the form has no rule where
        // its author believes there is one. Not silence, because the name is one the contract itself
        // declares, so reporting it costs no forward compatibility: a document may carry members this
        // reader has never heard of, and these are not that.
        //
        // `min` and `max` are left out: they are legitimate members of a number field, describing the
        // control's range, so the same word means two things by design.
        for (const misplaced of ["required", "email", "minLength", "maxLength", "pattern"]) {
            if (f[misplaced] === undefined)
                continue;
            warnDev(`Dynamic field "${f.name}" declares "${misplaced}" on the field, and it belongs in "validators" — ` +
                `nothing reads it here, so the field has no such rule.`, at, "MDY_DYNAMIC_MISPLACED_VALIDATOR");
        }
        // A constraint that cannot fail. A kind whose empty is a usable value — a slider, whose thumb is
        // always somewhere — starts at a value `required` accepts, so the rule can never refuse
        // anything. Nothing is lost and nothing leaks; what an author loses is the belief that a choice
        // is compulsory, on a form that is submitted by somebody who never touched the control.
        if (f.validators?.required === true
            && MDY_FIELD_KINDS.includes(f.kind)
            // The **kind's** empty, not this field's initial: `mdyEmptyValueFor` answers with a declared
            // initial when there is one, and a row that starts with values in it would otherwise read as a
            // field whose `required` cannot fail — which is a statement about the kind, not about a value
            // somebody put there.
            && required()(mdyEmptyValueFor({ name: f.name, kind: f.kind })).length === 0) {
            warnDev(`Dynamic field "${f.name}" declares "required", and a "${String(f.kind)}" starts at a value ` +
                `that satisfies it — the rule can never refuse anything, so the field is not compulsory.`, at, "MDY_DYNAMIC_CONSTRAINT_CANNOT_FAIL");
        }
        // A granularity that cannot be honoured is refused where it is written. A step that does not
        // divide its unit produces a rule its author did not write — `minuteStep: 7` offers 0, 7 … 56
        // and then jumps four minutes — and a picker merely behaving oddly at 56 past sends whoever
        // declared it looking in the wrong place. The field is kept and the granularity dropped: taking
        // the field away over a refinement removes something the user can see, over a rule they cannot.
        const declaredGranularity = f.granularity;
        if (declaredGranularity !== undefined) {
            const said = f.kind === "timepicker"
                ? validateTimeGranularity(declaredGranularity)
                    .map((problem) => explainGranularityProblem(problem)).join("; ")
                : `a "${String(f.kind)}" has no times to offer, which this contract does not declare`;
            // On any kind but a timepicker the member is one the contract does not declare for that kind,
            // so the finding is an unknown member, here and for format and viewMode below; only on a
            // timepicker is it a value the picker cannot honour.
            if (said.length > 0) {
                warnDev(`Dynamic field "${f.name}" declares a granularity this reader cannot honour: ${said}. ` +
                    "The field offers every time instead.", at, f.kind === "timepicker" ? "MDY_DYNAMIC_UNHONOURABLE_GRANULARITY" : "MDY_DYNAMIC_UNKNOWN_MEMBER");
                // Recorded, not deleted: the document belongs to the caller, and a parser that edits it
                // leaves a second read of the same object answering differently from the first.
                droppedGranularity.add(item);
            }
        }
        // A format is one of two clocks, and a field that is not a picker of times has no clock at all.
        // Dropped rather than corrected for the same reason the granularity is: the field stays, and the
        // refinement nobody can honour goes, so what is drawn is the documented default rather than a
        // guess at what the author meant.
        const declaredFormat = f.format;
        if (declaredFormat !== undefined) {
            const said = f.kind !== "timepicker"
                ? `a "${String(f.kind)}" draws no clock, which this contract does not declare`
                : declaredFormat === "12h" || declaredFormat === "24h"
                    ? ""
                    : `${JSON.stringify(declaredFormat)} is neither "12h" nor "24h"`;
            if (said.length > 0) {
                warnDev(`Dynamic field "${f.name}" declares a format this reader cannot honour: ${said}. ` +
                    "The field draws the 24-hour clock instead.", at, f.kind === "timepicker" ? "MDY_DYNAMIC_UNHONOURABLE_FORMAT" : "MDY_DYNAMIC_UNKNOWN_MEMBER");
                droppedFormat.add(item);
            }
        }
        // The view a picker opens in, refused the same way and for the same reason: a name that is not
        // one of the two views describes a control this contract cannot draw, and dropping the member
        // leaves the field opening on the dial rather than removing the field.
        const declaredViewMode = f.viewMode;
        if (declaredViewMode !== undefined) {
            const said = f.kind !== "timepicker"
                ? `a "${String(f.kind)}" has no views to open in, which this contract does not declare`
                : declaredViewMode === "dial" || declaredViewMode === "input"
                    ? ""
                    : `${JSON.stringify(declaredViewMode)} is neither "dial" nor "input"`;
            if (said.length > 0) {
                warnDev(`Dynamic field "${f.name}" declares a view this reader cannot open: ${said}. ` +
                    "The field opens on the dial instead.", at, f.kind === "timepicker" ? "MDY_DYNAMIC_UNOPENABLE_VIEW" : "MDY_DYNAMIC_UNKNOWN_MEMBER");
                droppedViewMode.add(item);
            }
        }
        const needsOptions = ["select", "radio", "multiselect", "segmented"];
        if (needsOptions.includes(f.kind)) {
            const options = f.options;
            if (!hasValidOptions(options)) {
                warnDev(`Dropped dynamic field "${f.name}": kind "${String(f.kind)}" requires a valid options array.`, at, "MDY_DYNAMIC_OPTIONS_REQUIRED");
                return false;
            }
            // Two options that say the same value are the same defect two fields sharing a name are: the
            // value builds an id — `s__option__pro` — so the second is unreachable, and the value itself
            // names two different things, which neither the control nor the submission can resolve. The
            // later ones are dropped, as the later of two fields with one name is.
            const seenValues = new Set();
            const kept = options.filter((option) => {
                const key = optionValueKey(option.value);
                if (!seenValues.has(key)) {
                    seenValues.add(key);
                    return true;
                }
                warnDev(`Dropped a duplicate option value ${key} on "${f.name}": an option's value is its identity, ` +
                    "so two options sharing one leave a value naming both and a control able to reach only the first.", at, "MDY_DYNAMIC_DUPLICATE_OPTION");
                return false;
            });
            if (kept.length !== options.length) {
                // Recorded, not written back: the document belongs to the caller, and a parser that edits it
                // leaves a second read of the same object answering differently from the first.
                dedupedOptions.set(item, kept);
            }
        }
        return true;
    });
    if (dedupedOptions.size === 0 && droppedGranularity.size === 0 && droppedFormat.size === 0 && droppedViewMode.size === 0)
        return accepted;
    return accepted.map((declared) => {
        const kept = dedupedOptions.get(declared);
        let out = kept === undefined ? declared : { ...declared, options: kept };
        if (droppedGranularity.has(declared)) {
            const { granularity: _dropped, ...rest } = out;
            out = rest;
        }
        if (droppedFormat.has(declared)) {
            const { format: _dropped, ...rest } = out;
            out = rest;
        }
        if (droppedViewMode.has(declared)) {
            const { viewMode: _dropped, ...rest } = out;
            out = rest;
        }
        return out;
    });
}
/**
 * What each refusal is called, and the phrase that identifies it.
 *
 * A consumer keys on the code; the message is prose and may be reworded. Deriving one from the other
 * inverts that — an edit to an English sentence silently renames a code somebody is matching on, and
 * nothing in a build objects.
 *
 * Each site that refuses something here names its code, so the code does not depend on the prose.
 * The phrases remain the recognisable part of each message — `dynamic-diagnostics.test.mjs` fails
 * when one stops appearing — and the fallback for a finding whose site names no code.
 */
export const MDY_DYNAMIC_DIAGNOSTICS = Object.freeze([
    Object.freeze({ code: "MDY_DYNAMIC_UNSUPPORTED_VERSION", phrase: "Unsupported dynamic form config version" }),
    Object.freeze({ code: "MDY_DYNAMIC_DUPLICATE_NAME", phrase: "duplicate dynamic field" }),
    Object.freeze({ code: "MDY_DYNAMIC_UNSAFE_NAME", phrase: "reserved or contains forbidden" }),
    Object.freeze({ code: "MDY_DYNAMIC_UNKNOWN_KIND", phrase: "unknown kind" }),
    Object.freeze({ code: "MDY_DYNAMIC_OPTIONS_REQUIRED", phrase: "requires a valid options" }),
    Object.freeze({ code: "MDY_DYNAMIC_DUPLICATE_OPTION", phrase: "duplicate option value" }),
    Object.freeze({ code: "MDY_DYNAMIC_MISPLACED_VALIDATOR", phrase: "belongs in \"validators\"" }),
    Object.freeze({ code: "MDY_DYNAMIC_CONSTRAINT_CANNOT_FAIL", phrase: "can never refuse anything" }),
    Object.freeze({ code: "MDY_DYNAMIC_UNKNOWN_PARSE_MODE", phrase: "is not one this reader knows" }),
    Object.freeze({ code: "MDY_DYNAMIC_INVALID_CONDITION", phrase: "expected an expression object" }),
    Object.freeze({ code: "MDY_DYNAMIC_UNDECLARED_CONTEXT", phrase: "does not declare in" }),
    Object.freeze({ code: "MDY_DYNAMIC_PATH_TOO_LONG", phrase: "a path may be" }),
    Object.freeze({ code: "MDY_DYNAMIC_PATTERN_TOO_LONG", phrase: "pattern length" }),
    Object.freeze({ code: "MDY_DYNAMIC_PATTERN_TOO_COSTLY", phrase: "backtracks exponentially" }),
    Object.freeze({ code: "MDY_DYNAMIC_COUNT_INCOMPLETE", phrase: "a floor and not a total" }),
    Object.freeze({ code: "MDY_DYNAMIC_UNKNOWN_MEMBER", phrase: "which this contract does not declare" }),
    Object.freeze({ code: "MDY_DYNAMIC_UNHONOURABLE_GRANULARITY", phrase: "granularity this reader cannot honour" }),
    Object.freeze({ code: "MDY_DYNAMIC_UNHONOURABLE_FORMAT", phrase: "format this reader cannot honour" }),
    Object.freeze({ code: "MDY_DYNAMIC_UNOPENABLE_VIEW", phrase: "view this reader cannot open" }),
]);
/** What a refusal is called when none of the named ones fits. */
export const MDY_DYNAMIC_INVALID_FIELD = "MDY_DYNAMIC_INVALID_FIELD";
/**
 * The code a finding is reported under: the one its site named, else the one its phrase identifies.
 *
 * Every refusal this reader makes names its code, because its messages quote what the document wrote
 * and a quoted name may carry another code's phrase. The phrase is the answer only for a finding
 * from a site that names none.
 */
function diagnosticCode(message, code) {
    if (code !== undefined)
        return code;
    return MDY_DYNAMIC_DIAGNOSTICS.find((entry) => message.includes(entry.phrase))?.code
        ?? MDY_DYNAMIC_INVALID_FIELD;
}
/**
 * Why an operator cannot read the value it was given, or nothing.
 *
 * A rule's `value` is the one member the operator consults at runtime, and a mismatch there does not
 * throw and does not warn: the condition simply answers the same thing forever. `in` against a
 * string is a membership test with no members, and a field whose rule can never fire is
 * indistinguishable from a field with no rule — except that the author believes they wrote one.
 *
 * The field's own kind is what makes the date check possible. Comparing dates is comparing strings,
 * and that is only sound while every string is the same shape: `"2026-2-01"` sorts before
 * `"2026-1-10"` because `"2"` sorts after `"1"` and the padding is what hides it.
 */
function ruleValueRefusal(operator, value, kind) {
    // A unary operator reads no value, so one written beside it is ignored rather than wrong — and a
    // generator that emits the same shape for every operator writes one. Refused, it would make a
    // document invalid for a member nothing consults.
    if (operator === "isEmpty" || operator === "isNotEmpty")
        return null;
    if (operator === "in" || operator === "notIn") {
        return Array.isArray(value) ? null : `${operator} tests membership of a list, and "value" is ${describeRuleValue(value)}.`;
    }
    const compares = operator === "greaterThan" || operator === "greaterThanOrEqual"
        || operator === "lessThan" || operator === "lessThanOrEqual";
    if (compares) {
        if (typeof value !== "number" && typeof value !== "string") {
            return `${operator} orders two numbers or two strings, and "value" is ${describeRuleValue(value)}.`;
        }
        if (typeof value === "string" && DATE_KINDS.has(kind ?? "") && !isIsoDate(value)) {
            return `${operator} on a ${kind} compares dates as text, so "value" must be a full ISO date (yyyy-MM-dd); "${value}" would order wrongly.`;
        }
    }
    return null;
}
/** The kinds whose value is an ISO date, so a comparison against them is a date comparison. */
const DATE_KINDS = new Set(["datepicker", "daterange"]);
function describeRuleValue(value) {
    if (value === undefined)
        return "missing";
    if (value === null)
        return "null";
    if (Array.isArray(value))
        return "an array";
    return `a ${typeof value}`;
}
function validFieldReference(name, names) {
    return typeof name === "string" && names.has(name);
}
/** The reason the last refusal carried, read by the caller straight after the call. */
let layoutRefusal = "reference";
/** Records why, and answers `false` so every existing refusal path keeps its shape. */
function refuse(reason) {
    layoutRefusal = reason;
    return false;
}
/** The diagnostic a refusal earns: the code names what was wrong, not what was written correctly. */
function layoutRefusalDiagnostic(reason, path, version) {
    if (reason === "version") {
        return {
            code: "MDY_DYNAMIC_UNSUPPORTED_VERSION",
            severity: "error",
            path,
            message: `layout uses a placement this document's version (${String(version)}) precedes; it needs version 3.`,
        };
    }
    if (reason === "depth") {
        return {
            code: "MDY_DYNAMIC_INVALID_LAYOUT",
            severity: "error",
            path,
            message: `layout nests deeper than ${MDY_LAYOUT_MAX_DEPTH} levels.`,
        };
    }
    if (reason === "shape") {
        return { code: "MDY_DYNAMIC_INVALID_LAYOUT", severity: "error", path, message: "layout node has an invalid shape." };
    }
    return {
        code: "MDY_DYNAMIC_UNKNOWN_FIELD_REFERENCE",
        severity: "error",
        path,
        message: "layout references an unknown or already-placed field.",
    };
}
/**
 * Refuses a layout nested deeper than the cap, for a caller that built one rather than read one.
 *
 * The cap is a statement about people, not about machines: nesting costs nothing measurable — the
 * tree grows by two elements a level and the indent does not grow at all — but nobody answers a
 * question whose applicability depends on six earlier answers. It therefore has to hold wherever a
 * structure arrives, and a document was only one of the two ways in.
 *
 * A caller assembling layout in code passed the cap in silence, so the limit read as a property of
 * the document format instead of a property of the framework, and the same form was legal or illegal
 * depending on how it had been written down. This says the same thing the parser says, at the other
 * door, in the terms a programmer can act on: a `throw` rather than a diagnostic, because there is no
 * document to annotate and no partial result worth returning.
 *
 * Deliberately not an option. Raising it is a decision about how deep a form a person can be asked to
 * fill in, and a per-call override turns that decision into a default nobody revisits — see
 * ADR 0160, which records what raising it would cost and what would have to be true first.
 */
export function assertLayoutWithinDepth(layout, cap = MDY_LAYOUT_MAX_DEPTH) {
    const walk = (node, depth, path) => {
        if (!isRecordValue(node))
            return;
        if (depth > cap) {
            throw new Error(`[modyra] A layout nests ${depth} levels deep at ${path}, and ${cap} is the limit. `
                + "The limit is about what a person can be asked to answer rather than what a browser can "
                + "draw: past this depth, whether a question applies depends on more earlier answers than "
                + "anybody holds in mind. Flatten the structure, or split the form into steps.");
        }
        const children = node.children;
        if (!Array.isArray(children))
            return;
        children.forEach((child, index) => walk(child, depth + 1, `${path}/children/${index}`));
    };
    (layout ?? []).forEach((node, index) => walk(node, 1, `/layout/${index}`));
}
function validLayoutNode(raw, names, seen, depth, allowSlots) {
    if (depth > MDY_LAYOUT_MAX_DEPTH)
        return refuse("depth");
    if (!isRecordValue(raw))
        return refuse("shape");
    const node = raw;
    if (typeof node.id !== "string")
        return refuse("shape");
    const slots = node.kind === "section"
        ? Array.isArray(node.children) ? [node.children] : []
        : node.kind === "columns"
            ? Array.isArray(node.columns) && node.columns.every(Array.isArray) ? node.columns : []
            : [];
    if (!slots.length && node.kind !== "section" && node.kind !== "columns")
        return refuse("shape");
    if (node.kind === "section" && !Array.isArray(node.children))
        return refuse("shape");
    if (node.kind === "columns" && (!Array.isArray(node.columns) || !node.columns.every(Array.isArray)))
        return refuse("shape");
    // `at` is untrusted like everything else here: a track count that is not a small positive integer
    // would reach the renderer as a custom property and produce a grid nobody asked for.
    if (node.kind === "columns" && node.at !== undefined) {
        if (!isRecordValue(node.at))
            return refuse("shape");
        for (const [size, count] of Object.entries(node.at)) {
            if (!isBreakpoint(size))
                return refuse("shape");
            if (typeof count !== "number" || !Number.isInteger(count) || count < 1 || count > MDY_MAX_LAYOUT_COLUMNS)
                return refuse("shape");
        }
    }
    // How many tracks this row has, so a slot cannot be sent to a column the row does not have. A
    // nested row is checked against its own count, which is why this is read here rather than passed
    // down. `at` may widen the row at a size, so the widest declared count is the ceiling.
    // `0` marks a node that has no tracks at all: a section, where placement cannot be honoured.
    const trackCount = node.kind === "columns"
        ? Math.max(Array.isArray(node.columns) ? node.columns.length : 1, ...Object.values((node.at ?? {})))
        : 0;
    for (const slot of slots) {
        for (const child of slot) {
            if (typeof child === "string") {
                if (!validFieldReference(child, names) || seen.has(child))
                    return refuse("reference");
                seen.add(child);
            }
            else if (isRecordValue(child) && "ref" in child) {
                // A v3 slot. Refused outright below v3: accepting it would make this parser disagree with
                // every other reader of the same document about what the contract says.
                if (!allowSlots)
                    return refuse("version");
                if (!validSlot(child, names, seen, trackCount))
                    return false;
            }
            else {
                if (!validLayoutNode(child, names, seen, depth + 1, allowSlots))
                    return false;
                // A section's `at` describes the column *this* node gives it, not its own children, so it is
                // checked here rather than inside its own validation. A nested row's `at` is a track count
                // and belongs to that row, which is why only a section is asked. Below v3 the key does not
                // exist, exactly as for a slot.
                const nested = child;
                if (nested.kind === "section" && nested.at !== undefined) {
                    if (!allowSlots)
                        return refuse("version");
                    if (!validPlacement(nested.at, trackCount))
                        return refuse("shape");
                }
            }
        }
    }
    return true;
}
/**
 * Validates one v3 slot: a real field, placed once, and placement that a row can honour.
 *
 * `trackCount` of 0 means the slot is not in a columns row. Placement is refused there rather than
 * accepted and ignored: `grid-column` and `display` belong to a grid item, and the column is the only
 * element the contract owns — a section's child is a field's own root, which the layout does not get
 * to restyle. A slot with no `at` is still fine anywhere; it is simply a field name written longhand.
 */
function validSlot(raw, names, seen, trackCount) {
    const slot = raw;
    if (!validFieldReference(slot.ref, names) || seen.has(slot.ref))
        return false;
    if (!validPlacement(slot.at, trackCount))
        return false;
    seen.add(slot.ref);
    return true;
}
/**
 * A per-size placement, whether it came from a slot or from a section occupying a column.
 *
 * One function because it is one rule: what a column may be told to do does not depend on what is
 * inside it. `trackCount` of 0 means there is no column, and placement is refused outright.
 */
function validPlacement(at, trackCount) {
    if (at === undefined)
        return true;
    if (trackCount === 0)
        return false;
    if (!isRecordValue(at))
        return false;
    for (const [size, placement] of Object.entries(at)) {
        if (!isBreakpoint(size))
            return false;
        if (!isRecordValue(placement))
            return false;
        const { column, hidden } = placement;
        if (column !== undefined && (!Number.isInteger(column) || column < 1 || column > trackCount))
            return false;
        if (hidden !== undefined && typeof hidden !== "boolean")
            return false;
        // A size that says nothing is a mistake worth reporting rather than a no-op to keep: it is
        // usually a typo for a size that meant something.
        if (column === undefined && hidden === undefined)
            return false;
    }
    return true;
}
/**
 * What is wrong with a document's tree, in two kinds.
 *
 * `structural` is about the shape the walk needs — a root that is not a node, a group with no
 * children, an item a collection does not have. Any of them and there is nothing to walk, so the
 * document is turned down whole.
 *
 * `perField` is about one declaration: a kind nobody named, a validator of the wrong shape, or a
 * group's child that is not a node at all. The walk still runs and every other field survives, which
 * is what makes the counts mean what they say — one field refused out of three is not three fields
 * lost. A malformed child is a sibling's problem no more than an unknown kind is: the group around it
 * is still a group, and the walk skips what it cannot read.
 *
 * `rejected` counts declarations, not findings: a field kept with a finding about an option it
 * dropped is still accepted, and a field refused for two reasons is still one field.
 */
function validateDynamicSchema(input) {
    const out = [];
    const perField = [];
    let rejected = 0;
    /** Every context key the document's conditions read, for the envelope's declaration to match. */
    const contextRead = new Set();
    const stack = [
        { raw: input, path: "/schema", name: "schema", scope: new Set() },
    ];
    const push = (frames) => {
        for (let index = frames.length - 1; index >= 0; index -= 1)
            stack.push(frames[index]);
    };
    while (stack.length > 0) {
        const { raw, path, name, scope, child } = stack.pop();
        if (!isDynamicNode(raw)) {
            const finding = { code: "MDY_DYNAMIC_INVALID_NODE", severity: "error", path, message: "node must be field, group, array, or record." };
            if (child) {
                perField.push(finding);
                rejected += 1;
            }
            else {
                out.push(finding);
            }
            continue;
        }
        // A clause a document writes on this node. Checked here because this is where the walk knows
        // what encloses it: an expression is read against that, so the names it may use are the enclosing
        // group's children — inside a collection's `item`, the row's own cells.
        for (const slot of ["when", "asyncWhen"]) {
            const clause = raw[slot];
            if (clause === undefined)
                continue;
            const problems = validateExpression(clause, `${path}/${slot}`);
            if (problems.length > 0) {
                out.push({
                    code: "MDY_DYNAMIC_INVALID_CONDITION",
                    severity: "error",
                    path: `${path}/${slot}`,
                    message: problems.join("; "),
                });
                continue;
            }
            const unreadable = expressionPaths(clause)
                .filter((read) => !scope.has(read.split(".")[0] ?? read));
            if (unreadable.length > 0) {
                out.push({
                    code: "MDY_DYNAMIC_UNKNOWN_FIELD_REFERENCE",
                    severity: "error",
                    path: `${path}/${slot}`,
                    message: `condition reads ${unreadable.map((read) => `"${read}"`).join(", ")}, which nothing ` +
                        "encloses this clause declares — a condition is read against what encloses it, and " +
                        '{ "root": true } is how it reaches the whole form.',
                });
            }
            for (const key of expressionContextKeys(clause))
                contextRead.add(key);
        }
        if (raw["node"] === "field") {
            if (!isRecordValue(raw["field"])) {
                out.push({ code: "MDY_DYNAMIC_INVALID_FIELD", severity: "error", path: `${path}/field`, message: "field node requires a field object." });
                continue;
            }
            // Every check a field gets in a flat list, applied wherever the field is. The walk knew a
            // node's *shape* and left what the field declares to the flat reader, which never sees a cell
            // inside a collection: a `kind` nobody declared, or a `validators.pattern` that is a number,
            // parsed clean at any depth below a row and then met the engine — where a person is already
            // waiting — or produced a control nobody asked for.
            // Named by the key its parent gave it, which the frame carries. A field in a tree has no `name`
            // of its own — the key is the name — and a key may hold "/", so the path cannot give it back:
            // `a/b` would read as `b`, and `x/` as a field without a name.
            const kept = collectingDiagnostics((message, _path, code) => perField.push({
                code: diagnosticCode(message, code),
                severity: "error",
                path: `${path}/field`,
                message,
            }), () => parseDynamicFields([{ ...raw["field"], name }]));
            if (kept.length === 0)
                rejected += 1;
            // The initial the document declares, against the shape the kind holds. A record's initial is
            // measured against its own shape and an array's against its own; a field's was measured
            // against nothing, so a text field declaring `42` passed in the strictest mode there is and
            // produced a form that was invalid before anybody touched it — "This field holds string", on
            // a value the user never entered.
            const declared = raw["field"];
            if (declared.initialValue !== undefined
                && MDY_FIELD_KINDS.includes(declared.kind)) {
                const mismatch = explainValueMismatch(declared.kind, declared.initialValue);
                if (mismatch !== null) {
                    out.push({
                        code: "MDY_DYNAMIC_INVALID_FIELD",
                        severity: "error",
                        path: `${path}/field/initialValue`,
                        message: `field initialValue does not match the kind — ${mismatch}.`,
                    });
                }
            }
            continue;
        }
        if (raw["node"] === "group") {
            if (!isRecordValue(raw["children"])) {
                out.push({ code: "MDY_DYNAMIC_INVALID_GROUP", severity: "error", path, message: "group requires children." });
                continue;
            }
            const children = [];
            const here = new Set(Object.keys(raw["children"]));
            for (const [key, child] of Object.entries(raw["children"])) {
                const at = `${path}/children/${pointerSegment(key)}`;
                if (!isSafeDynamicName(key))
                    out.push({ code: "MDY_DYNAMIC_UNSAFE_NAME", severity: "error", path: at, message: "unsafe child name." });
                else
                    children.push({ raw: child, path: at, name: key, scope: here, child: true });
            }
            push(children);
            continue;
        }
        if (raw["node"] === "record") {
            if (!isRecordValue(raw["item"]))
                out.push({ code: "MDY_DYNAMIC_INVALID_RECORD", severity: "error", path, message: "record requires an item node." });
            else
                push([{ raw: raw["item"], path: `${path}/item`, name: "item", scope: new Set() }]);
            const initial = raw["initialValue"];
            if (initial !== undefined && !isRecordValue(initial))
                out.push({ code: "MDY_DYNAMIC_INVALID_RECORD", severity: "error", path: `${path}/initialValue`, message: "record initialValue must be an object keyed by row key." });
            else if (isRecordValue(initial)) {
                if (Object.keys(initial).length > MDY_MAX_DYNAMIC_WRITTEN_ROWS)
                    out.push(writtenRowsLimit(path, "record"));
                for (const key of Object.keys(initial)) {
                    // A key that cannot be a path segment names a row nothing can address.
                    if (!isSafeDynamicName(key))
                        out.push({ code: "MDY_DYNAMIC_UNSAFE_NAME", severity: "error", path: `${path}/initialValue/${key}`, message: "unsafe row key." });
                }
            }
            continue;
        }
        if (!isRecordValue(raw["item"]))
            out.push({ code: "MDY_DYNAMIC_INVALID_ARRAY", severity: "error", path, message: "array requires an item node." });
        else
            push([{ raw: raw["item"], path: `${path}/item`, name: "item", scope: new Set() }]);
        if (raw["initialValue"] !== undefined && !Array.isArray(raw["initialValue"]))
            out.push({ code: "MDY_DYNAMIC_INVALID_ARRAY", severity: "error", path: `${path}/initialValue`, message: "array initialValue must be an array." });
        else if (Array.isArray(raw["initialValue"]) && raw["initialValue"].length > MDY_MAX_DYNAMIC_WRITTEN_ROWS)
            out.push(writtenRowsLimit(path, "array"));
    }
    return { structural: out, perField, rejected, contextRead: [...contextRead] };
}
/** Parses v1/v2 untrusted input with structured diagnostics. */
export function parseDynamicForm(input, options = {}) {
    const diagnostics = [];
    /**
     * A member nobody declared, reported where it is written.
     *
     * The published JSON Schema closes every one of these objects and an editor says so while the
     * document is being typed. A document from a CMS, a model or a server met neither the type nor the
     * editor, and the parser — the only check it does meet — passed the same member without a word.
     */
    const reportUnknownMembers = (value, at, what) => {
        const members = at.startsWith("/layout")
            // A slot is the third shape a layout position can take, beside the two node kinds: it names a
            // field and says where it sits. `ref` is what separates it from a node, which always has a
            // `kind`.
            ? value.kind === "columns"
                ? MDY_DYNAMIC_MEMBERS.layoutColumns
                : value.kind === undefined && isRecordValue(value) && "ref" in value
                    ? MDY_DYNAMIC_MEMBERS.layoutSlot
                    : MDY_DYNAMIC_MEMBERS.layoutSection
            : at.startsWith("/rules")
                ? MDY_DYNAMIC_MEMBERS.rule
                : MDY_DYNAMIC_MEMBERS.validation;
        const unknown = unknownMembers(value, members);
        if (unknown.length === 0)
            return;
        diagnostics.push({
            code: "MDY_DYNAMIC_UNKNOWN_MEMBER",
            severity: "error",
            path: at,
            message: `${what} carries ${unknown.map((member) => JSON.stringify(member)).join(", ")}, which this ` +
                "contract does not declare, so nothing reads it.",
        });
    };
    /**
     * Every layout node and every slot under one, each held to its own published list.
     *
     * Reported at every depth, not only at the top: a document's layout nests, and a member outside
     * the list is a member nothing reads wherever it is written. A slot is where it costs the most —
     * `at` is how a field says which column it takes at which size, so `att` is a placement that never
     * happens, and the node parses clean with the misspelling kept and handed to whatever draws it.
     *
     * Over a stack rather than by recursion: the depth here is the document's own, and a document is
     * untrusted input. The traversal is bounded by the same depth the validator enforces, so a layout
     * deeper than the contract allows is refused there rather than walked here.
     */
    const reportLayoutMembers = (root, rootPath) => {
        const pending = [{ node: root, at: rootPath, depth: 1 }];
        while (pending.length > 0) {
            const { node, at, depth } = pending.pop();
            if (!isRecordValue(node) || depth > MDY_LAYOUT_MAX_DEPTH)
                continue;
            reportUnknownMembers(node, at, "kind" in node ? "a layout node" : "a layout slot");
            // Read structurally: the two node kinds have incompatible `kind` literals, so the intersection
            // of their declared types is uninhabited, and what this walk needs is only where children sit.
            const shape = node;
            if (Array.isArray(shape.children)) {
                shape.children.forEach((child, index) => {
                    pending.push({ node: child, at: `${at}/children/${index}`, depth: depth + 1 });
                });
                continue;
            }
            if (!Array.isArray(shape.columns))
                continue;
            shape.columns.forEach((column, track) => {
                if (!Array.isArray(column))
                    return;
                column.forEach((child, index) => {
                    pending.push({ node: child, at: `${at}/columns/${track}/${index}`, depth: depth + 1 });
                });
            });
        }
    };
    // A mode nobody declared is not lenient. `strict` is what a publishing gate asks for, and the
    // answer to a typo in it — or to the options object being a bare string, or `null` — was a lenient
    // parse reported as a success, so a contract nobody checked went out with `ok: true` beside it.
    //
    // Reported rather than thrown: this parser's whole design is a report. The report is what makes
    // `ok` false, which is the half that closes the gate.
    // Read only from where the signature puts it. A bare `"strict"` in the options position is a
    // caller's mistake, and honouring it would invent a second spelling of the argument — one nobody
    // documented and every later reader would have to know about. It is reported, which is the half
    // that matters: what must not happen is a lenient parse answering `ok: true` to a caller who asked
    // for a gate.
    const asked = typeof options === "object" && options !== null
        ? options.mode
        : options === undefined ? undefined : options;
    const optionsAreAnObject = options === undefined
        || (typeof options === "object" && options !== null && !Array.isArray(options));
    const modeUnderstood = optionsAreAnObject
        && (asked === undefined || asked === "strict" || asked === "lenient");
    if (!modeUnderstood) {
        diagnostics.push({
            code: "MDY_DYNAMIC_UNKNOWN_PARSE_MODE",
            severity: "error",
            path: "/options/mode",
            message: `parse mode ${JSON.stringify(asked)} is not one this reader knows — it is "strict" or ` +
                `"lenient". The document was read leniently, which is not what was asked for.`,
        });
    }
    const rawEnvelope = typeof input === "object" && input !== null && !Array.isArray(input)
        ? input
        : undefined;
    /**
     * A version this reader does not have, answered as a version.
     *
     * The document is refused either way — nothing a newer publisher wrote is read as if it were this
     * contract — but which refusal it gets decides where the host looks: read as a malformed field
     * list, a document from a publisher one version ahead sent its reader hunting for the broken
     * field, when the answer is that this reader does not have that version at all.
     */
    const versionDeclared = rawEnvelope !== undefined && "version" in rawEnvelope
        ? rawEnvelope.version
        : undefined;
    const versionUnderstood = versionDeclared === undefined || isSupportedVersion(versionDeclared);
    if (!versionUnderstood) {
        return {
            ok: false,
            version: null,
            fields: [],
            collections: [],
            layout: [],
            rules: [],
            validations: [],
            acceptedCount: 0,
            rejectedCount: 0,
            diagnostics: [{
                    code: "MDY_DYNAMIC_UNSUPPORTED_VERSION",
                    severity: "error",
                    path: "/version",
                    message: `Unsupported dynamic form config version ${JSON.stringify(versionDeclared)} — this contract ` +
                        `has versions ${SUPPORTED_VERSIONS_PHRASE}, and nothing in this document was read. ` +
                        (versionDeclared === 1
                            // The one refusal people will meet with a document that worked yesterday, so it carries
                            // the whole migration rather than the fact of the refusal: version 1 differs from 2 in
                            // the number and in nothing else.
                            ? 'Version 1 was read by this runtime alone and is no longer supported — set "version": 2, ' +
                                "which reads the same fields."
                            : "The document is from a publisher this reader does not know."),
                }],
        };
    }
    let collections = [];
    /** How many fields a tree document declared, kept or not — `undefined` until a tree is walked. */
    let declaredLeaves;
    /**
     * How many of those the parser turned down.
     *
     * Counted from what was *reported*, not from the difference between declared and kept: a
     * collection's cells are declared and never become flat fields, so the difference would call every
     * one of them a rejection and a correct document would read as having lost everything.
     */
    let treeRejected = 0;
    let fields = collectingDiagnostics(
    // `/fields` only when the reporter did not say which entry: an envelope-level refusal is about
    // the list, and a field's own is about the field.
    (message, path, code) => diagnostics.push({
        code: diagnosticCode(message, code), severity: "error", path: path ?? "/fields", message,
    }), () => isSupportedVersion(rawEnvelope?.version) && rawEnvelope.schema !== undefined
        ? []
        : parseDynamicFields(input));
    const envelope = typeof input === "object" && input !== null && !Array.isArray(input)
        ? input
        : undefined;
    // A bare field array declares no version and is the shape most callers pass; it is read as the
    // flat document it is. A declared `version: 1` never reaches here — it is refused above, because a
    // version two of the three runtimes refuse is not a version this contract has (ADR 0136).
    const version = Array.isArray(input)
        ? 1 : isSupportedVersion(envelope?.version) ? envelope.version : null;
    // v3 is v2 plus per-slot placement: every envelope member is read the same way, and only the
    // layout validator is told which vocabulary the document is entitled to use.
    const structured = version !== null && version !== 1;
    if (structured && envelope?.schema !== undefined) {
        const { structural, perField, rejected, contextRead } = validateDynamicSchema(envelope.schema);
        diagnostics.push(...structural, ...perField);
        // What the document said it had, counted before anything is refused. A schema the validator
        // turns down wholesale never reaches the walk, so without this a document declaring three
        // children reported none accepted and none rejected — three entered and nothing came out, with
        // the counts saying nothing happened.
        const declaration = declaredFieldCount(envelope.schema);
        declaredLeaves = declaration.count;
        if (!declaration.complete) {
            diagnostics.push({
                code: "MDY_DYNAMIC_COUNT_INCOMPLETE",
                severity: "warning",
                path: "/schema",
                message: `The document declares more than the ${MDY_MAX_DECLARATION_WALK} declarations this reader ` +
                    "counts, so what it accepted and what it turned down are a floor and not a total.",
            });
        }
        // A schema refused whole never reaches the walk, so everything it declared was turned down.
        // What the document says it needs from the host, against what its conditions read. The
        // declaration is an API between the application and whoever authors documents for it, so a key
        // read and not declared is a promise the document did not make and the host cannot keep.
        const declared = new Set(Array.isArray(envelope.requiresContext)
            ? envelope.requiresContext.filter((key) => typeof key === "string")
            : []);
        const undeclared = contextRead.filter((key) => !declared.has(key));
        if (undeclared.length > 0) {
            diagnostics.push({
                code: "MDY_DYNAMIC_UNDECLARED_CONTEXT",
                severity: "error",
                path: "/requiresContext",
                message: `conditions read context ${undeclared.map((key) => `"${key}"`).join(", ")}, which this ` +
                    "document does not declare in \"requiresContext\" — a host cannot supply what it is not told about.",
            });
        }
        // A shape the walk cannot enter turns the document down whole; a field that declares something
        // nobody can render is one field, and the count says so.
        treeRejected = structural.length > 0 ? declaredLeaves : rejected;
        if (structural.length === 0) {
            // The walk reports what only it can see — a row's value a cell cannot hold, a path too long —
            // under the collector, so a leaf dropped there is said and counted. Each such finding is one
            // leaf dropped. `declaredLeaves` is what a document said it had, so the counts a consumer
            // reports with are about the document and not about what survived it.
            const before = diagnostics.length;
            const walked = collectingDiagnostics((message, path, code) => diagnostics.push({
                code: diagnosticCode(message, code), severity: "error", path: path ?? "/schema", message,
            }), () => {
                try {
                    return flattenTree(envelope.schema, false);
                }
                catch (error) {
                    if (error instanceof MdyDynamicSchemaLimitError)
                        return error;
                    throw error;
                }
            });
            if (walked instanceof MdyDynamicSchemaLimitError) {
                // What the walk said before it stopped is about a part of the tree, not the document.
                diagnostics.length = before;
                diagnostics.push({ code: walked.code, severity: "error", path: walked.path, message: walked.message });
                treeRejected = declaredLeaves;
            }
            else {
                fields = walked.fields;
                collections = walked.collections;
                treeRejected = rejected + diagnostics.length - before;
                declaredLeaves = Math.max(declaredLeaves ?? 0, walked.fields.length + treeRejected);
            }
        }
    }
    const names = new Set(fields.map((field) => field.name));
    /** What each declared field holds, so a rule's value can be judged against the field it reads. */
    const kindOf = new Map(fields.map((field) => [field.name, field.kind]));
    const layout = [];
    const rules = [];
    const validations = [];
    /** Fields already placed by an accepted layout node — a field belongs in exactly one slot. */
    const placed = new Set();
    // A member no version of this contract has. A document reaching for something the contract does
    // not do — a computation, a slot an author expected to exist — parsed clean and rendered a form
    // that quietly did not do it.
    if (envelope !== undefined) {
        const unknown = unknownMembers(envelope, MDY_DYNAMIC_MEMBERS.document);
        if (unknown.length > 0) {
            diagnostics.push({
                code: "MDY_DYNAMIC_UNKNOWN_MEMBER",
                severity: "error",
                path: "/",
                message: `the document carries ${unknown.map((member) => JSON.stringify(member)).join(", ")}, which ` +
                    "this contract does not declare, so nothing reads it.",
            });
        }
    }
    // A member the document's version predates, on the envelope. `requiresContext` arrived with v4 and
    // is read by the builder, so a v2 or v3 document carrying it declares a need nothing acts on.
    if (envelope?.requiresContext !== undefined && version !== null && version < 4) {
        diagnostics.push({
            code: "MDY_DYNAMIC_UNSUPPORTED_VERSION",
            severity: "error",
            path: "/requiresContext",
            message: `Unsupported dynamic form config version for "requiresContext": it arrived with version 4, ` +
                `and this document says ${version}. Set "version": 4 to use it.`,
        });
    }
    if (!structured && envelope) {
        for (const slot of ["layout", "rules", "validations"]) {
            if (envelope[slot] === undefined)
                continue;
            diagnostics.push({
                code: "MDY_DYNAMIC_UNSUPPORTED_VERSION",
                severity: "error",
                path: `/${slot}`,
                // The registry's phrase for this code has to appear in the sentence: a consumer keys on the
                // code, and `dynamic-diagnostics.test.mjs` is what keeps the two from drifting apart.
                message: `Unsupported dynamic form config version for "${slot}": a document that declares no version reads fields and nothing else. Set "version": 2 to use it.`,
            });
        }
    }
    if (structured && envelope) {
        if (envelope.layout !== undefined && !Array.isArray(envelope.layout)) {
            diagnostics.push({ code: "MDY_DYNAMIC_INVALID_LAYOUT", severity: "error", path: "/layout", message: "layout must be an array." });
        }
        else
            for (const [index, raw] of (envelope.layout ?? []).entries()) {
                if (typeof raw !== "object" || raw === null) {
                    diagnostics.push({ code: "MDY_DYNAMIC_INVALID_LAYOUT", severity: "error", path: `/layout/${index}`, message: "layout node must be an object." });
                    continue;
                }
                // A node at the top of the layout sits in no column, so it has nothing to be placed in — the
                // same reason a slot's `at` is refused outside a row, checked here because this is the one
                // place a layout node is visited without a parent.
                if (raw.at !== undefined && raw.kind === "section") {
                    diagnostics.push({ code: "MDY_DYNAMIC_INVALID_LAYOUT", severity: "error", path: `/layout/${index}`, message: "a section at the top of the layout occupies no column and cannot be placed." });
                    continue;
                }
                reportLayoutMembers(raw, `/layout/${index}`);
                layoutRefusal = "reference";
                if (!validLayoutNode(raw, names, placed, 1, version !== null && version >= 3)) {
                    diagnostics.push(layoutRefusalDiagnostic(layoutRefusal, `/layout/${index}`, version));
                    continue;
                }
                layout.push(raw);
            }
        if (envelope.rules !== undefined && !Array.isArray(envelope.rules)) {
            diagnostics.push({ code: "MDY_DYNAMIC_INVALID_RULE", severity: "error", path: "/rules", message: "rules must be an array." });
        }
        else
            for (const [index, raw] of (envelope.rules ?? []).entries()) {
                if (typeof raw !== "object" || raw === null) {
                    diagnostics.push({ code: "MDY_DYNAMIC_INVALID_RULE", severity: "error", path: `/rules/${index}`, message: "rule must be an object." });
                    continue;
                }
                reportUnknownMembers(raw, `/rules/${index}`, "a rule");
                const rule = raw;
                const effects = ["visible", "hidden", "enabled", "disabled"];
                const operators = ["equals", "notEquals", "in", "notIn", "isEmpty", "isNotEmpty", "greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual"];
                if (!effects.includes(rule.effect ?? "") || !validFieldReference(rule.target, names) || !rule.when || !validFieldReference(rule.when.field, names) || !operators.includes(rule.when.operator)) {
                    diagnostics.push({ code: "MDY_DYNAMIC_INVALID_RULE", severity: "error", path: `/rules/${index}`, message: "rule has an unsupported effect/operator or references an unknown field." });
                    continue;
                }
                // The part the operator actually reads. Four of a rule's five members were guarded and this
                // one was not, so `greaterThan` against an object and `in` against a string parsed clean in
                // strict mode and then answered `false` forever — a rule that cannot fire, reported as a rule.
                const valueRefusal = ruleValueRefusal(rule.when.operator, rule.when.value, kindOf.get(rule.when.field));
                if (valueRefusal) {
                    diagnostics.push({ code: "MDY_DYNAMIC_INVALID_RULE", severity: "error", path: `/rules/${index}/when/value`, message: valueRefusal });
                    continue;
                }
                rules.push(raw);
            }
        if (envelope.validations !== undefined && !Array.isArray(envelope.validations)) {
            diagnostics.push({ code: "MDY_DYNAMIC_INVALID_VALIDATION", severity: "error", path: "/validations", message: "validations must be an array." });
        }
        else
            for (const [index, raw] of (envelope.validations ?? []).entries()) {
                const at = `/validations/${index}`;
                if (typeof raw !== "object" || raw === null) {
                    diagnostics.push({ code: "MDY_DYNAMIC_INVALID_VALIDATION", severity: "error", path: at, message: "validation must be an object." });
                    continue;
                }
                reportUnknownMembers(raw, at, "a validation");
                const validation = raw;
                if (typeof validation.message !== "string" || validation.message.trim() === "") {
                    diagnostics.push({ code: "MDY_DYNAMIC_INVALID_VALIDATION", severity: "error", path: at, message: "validation needs a non-empty message." });
                    continue;
                }
                // A target names a field, the same way a rule's does. An unknown one would attach the error to
                // nothing and the user would never see why the form will not submit.
                if (validation.target !== undefined && !validValidationPath(validation.target, names)) {
                    diagnostics.push({ code: "MDY_DYNAMIC_UNKNOWN_FIELD_REFERENCE", severity: "error", path: at, message: "validation target references an unknown field." });
                    continue;
                }
                const problems = validateExpression(validation.when, `${at}.when`);
                if (problems.length > 0) {
                    diagnostics.push({ code: "MDY_DYNAMIC_INVALID_VALIDATION", severity: "error", path: at, message: problems.join("; ") });
                    continue;
                }
                // Every path the condition reads must exist, for the same reason: a condition asking about a
                // field that is not in the form is a rule that can never be satisfied, and it fails silently.
                // The empty path is the whole form value, and a rule's `field` has never accepted it — so the
                // same condition was a condition in one half of the document format and not in the other, and
                // the half that took it produced a check that can never fire: comparing the form object to a
                // scalar is false for every value the form can be driven to. `{ root: true }` is the operand
                // that reads the whole form, and it says so.
                const unknown = expressionPaths(validation.when).filter((path) => !validValidationPath(path, names));
                if (unknown.length > 0) {
                    diagnostics.push({
                        code: "MDY_DYNAMIC_UNKNOWN_FIELD_REFERENCE",
                        severity: "error",
                        path: at,
                        message: `validation condition references unknown field(s): ${unknown.map((path) => `"${path}"`).join(", ")}` +
                            `${unknown.includes("") ? ' — the whole form value is { "root": true }' : ""}.`,
                    });
                    continue;
                }
                validations.push(raw);
            }
    }
    const sourceCount = Array.isArray(input)
        ? input.length
        : Array.isArray(envelope?.fields)
            ? envelope.fields.length
            : declaredLeaves ?? fields.length;
    const elsewhere = diagnostics.filter((d) => d.path.startsWith("/layout/") || d.path.startsWith("/rules/") || d.path.startsWith("/validations/")).length;
    const rejectedCount = (declaredLeaves === undefined
        ? Math.max(0, sourceCount - fields.length)
        : treeRejected) + elsewhere;
    // A rule the document's version predates. `integer` arrived with v5, and a v2 document carrying it
    // is asking for a constraint no reader of that contract emits — the field would validate one way
    // here and another wherever the document's declared version is what is honoured.
    //
    // The arrival number is read from the contract rather than written here: the schema audit excuses
    // the published schemas from the same table, and two spellings of one arrival is the drift this
    // whole version exists to avoid.
    {
        const arrivedWith = MDY_DYNAMIC_MEMBER_ARRIVALS.validators?.integer;
        if (arrivedWith !== undefined && version !== null && version < arrivedWith) {
            // A tree is read declaration by declaration rather than from its flattened fields: a collection
            // with no rows flattens to none, and its template is still what every row it gains is read from.
            const paths = structured && envelope?.schema !== undefined
                ? fieldDeclarations(envelope.schema)
                    .filter(({ field }) => isRecordValue(field) && isRecordValue(field["validators"]) && field["validators"]["integer"] != null)
                    .map(({ path }) => path)
                : fields
                    .filter((field) => field.validators?.integer != null)
                    .map((field) => listEntryPath(Array.isArray(input) ? input : envelope?.fields, field.name) ?? "/fields");
            for (const at of paths) {
                const path = `${at}/validators/integer`;
                diagnostics.push({
                    code: "MDY_DYNAMIC_UNSUPPORTED_VERSION",
                    severity: "error",
                    path,
                    message: `Unsupported dynamic form config version for "integer": it arrived with version ` +
                        `${arrivedWith}, and this document says ${version}. Set "version": ${arrivedWith} to use it.`,
                });
            }
        }
    }
    const strict = modeUnderstood && asked === "strict";
    // What strict mode refuses on. A warning is published as the severity a consumer reads to tell
    // what must be fixed from what is worth knowing, so refusing the whole document for one turns the
    // distinction into a second word for "error" — and the document it refused had nothing malformed
    // in it, only more declarations than the reader counts.
    const refusals = diagnostics.filter((diagnostic) => diagnostic.severity === "error").length;
    return {
        // `ok` answers one question: did anything the parser graded as an error happen. It is false
        // whenever a diagnostic carries `severity: "error"`, in either mode, because a result that
        // grades a diagnostic an error and reports success in the same breath contradicts itself — and
        // the caller who reads `ok` and not the diagnostics is the one who then mounts a field whose
        // declared condition was dropped.
        //
        // It is not a statement about usability. A lenient parse still returns everything it could
        // read, and `acceptedCount`/`rejectedCount` still say how much that was; what `ok: false`
        // withdraws is only the claim that nothing was lost.
        ok: modeUnderstood && version !== null && refusals === 0,
        version,
        fields: strict && refusals > 0 ? [] : fields,
        layout: strict && refusals > 0 ? [] : layout,
        rules: strict && refusals > 0 ? [] : rules,
        validations: strict && refusals > 0 ? [] : validations,
        collections,
        diagnostics,
        // What the document declared and the parser understood. For a tree that is every field node it
        // declares, collections included, minus what was refused — `fields` cannot answer it, because a
        // collection's cells are not flat fields until rows exist.
        acceptedCount: declaredLeaves === undefined
            ? fields.length
            : Math.max(0, declaredLeaves - rejectedCount),
        rejectedCount,
    };
}
